/** Generation History — timestamped list of every asset generated in this workspace. */
import { useQuery } from "@tanstack/react-query";

import { listHistory } from "@/entities/ai-studio";
import { useCurrentWorkspace } from "@/entities/workspace";
import { Badge } from "@/shared/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";

export function GenerationHistoryPage(): JSX.Element {
  const ws = useCurrentWorkspace();
  const q = useQuery({
    queryKey: ["ai-studio-history", ws?.id],
    queryFn: () => listHistory(ws!.id, 100),
    enabled: !!ws,
  });

  if (!ws) return <div className="mx-auto max-w-3xl px-6 py-16 text-center text-muted-foreground">Select a workspace.</div>;

  return (
    <div className="mx-auto max-w-6xl px-6 py-10" data-testid="generation-history-page">
      <div className="mb-6 space-y-2">
        <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/60 px-3 py-0.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: ws.profile.primary_color }} />
          {ws.slug} · generation history
        </div>
        <h1 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">Generation History</h1>
        <p className="text-sm text-muted-foreground">Every asset AI Studio has generated for this workspace.</p>
      </div>

      {q.isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <Card className="border-border/70 bg-card/70">
          <CardHeader>
            <CardTitle className="text-sm">{q.data?.length ?? 0} results</CardTitle>
            <CardDescription className="text-xs">Sorted newest first</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="p-2">Timestamp</th>
                    <th className="p-2">Type</th>
                    <th className="p-2">Campaign</th>
                    <th className="p-2">Status</th>
                    <th className="p-2">Provider</th>
                    <th className="p-2">Model</th>
                    <th className="p-2">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data ?? []).map((a) => (
                    <tr key={a.id} data-testid={`history-row-${a.id}`} className="border-t border-border/40">
                      <td className="p-2 font-mono text-[10px]">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="p-2">{a.content_type}</td>
                      <td className="p-2">{a.campaign ?? "—"}</td>
                      <td className="p-2"><Badge variant="outline" className="text-[10px]">{a.status}</Badge></td>
                      <td className="p-2">{a.provider}</td>
                      <td className="p-2">{a.model}</td>
                      <td className="p-2">{a.estimated_cost_usd != null ? `$${a.estimated_cost_usd.toFixed(5)}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
